import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
} from '@/shared/components/ui/Card';
import {
  DataTable,
  DataTableHeader,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '@/shared/components/ui/DataTable';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { ListPagePagination } from '@/shared/components/ui/ListPagePagination';
import type { CampaignMessageListResponse, CampaignMessageResponse } from '@troia-v3/types';
interface CampaignMessagesTableProps {
  data?: CampaignMessageListResponse;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onPageChange: (page: number) => void;
}

const statusOptions = [
  { value: '', label: 'Todos os status' },
  { value: 'pending', label: 'Pendente' },
  { value: 'queued', label: 'Na fila' },
  { value: 'sending', label: 'Enviando' },
  { value: 'sent', label: 'Enviada' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'read', label: 'Lida' },
  { value: 'failed', label: 'Falhou' },
  { value: 'cancelled', label: 'Cancelada' },
];

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pendente', color: 'var(--theme-muted-text)', bgColor: 'var(--theme-button-ghost-hover)' },
  queued: { label: 'Na Fila', color: 'var(--theme-info-color)', bgColor: 'var(--theme-info-background)' },
  sending: { label: 'Enviando', color: 'var(--theme-warning-color)', bgColor: 'var(--theme-warning-background)' },
  sent: { label: 'Enviada', color: 'var(--theme-info-color)', bgColor: 'var(--theme-info-background)' },
  delivered: { label: 'Entregue', color: 'var(--theme-success-color)', bgColor: 'var(--theme-success-background)' },
  read: { label: 'Lida', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
  failed: { label: 'Falhou', color: 'var(--theme-error-color)', bgColor: 'var(--theme-error-background)' },
  cancelled: { label: 'Cancelada', color: 'var(--theme-error-color)', bgColor: 'var(--theme-error-background)' },
};

const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MessageStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = statusConfig[status] || statusConfig.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: config.bgColor,
        color: config.color,
      }}
    >
      {/* Mockup `.mst i`: dot 6px currentColor antes do label */}
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
      {config.label}
    </span>
  );
};

const MessageRow: React.FC<{ message: CampaignMessageResponse }> = ({ message }) => {
  return (
    <DataTableRow>
      <DataTableCell>
        <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
          {message.recipientName || '-'}
        </span>
      </DataTableCell>
      <DataTableCell>
        <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
          {message.recipientIdentifier}
        </span>
      </DataTableCell>
      <DataTableCell>
        <MessageStatusBadge status={message.status} />
      </DataTableCell>
      <DataTableCell>
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {formatDateTime(message.sentAt)}
        </span>
      </DataTableCell>
      <DataTableCell>
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {formatDateTime(message.deliveredAt)}
        </span>
      </DataTableCell>
      <DataTableCell>
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {formatDateTime(message.readAt)}
        </span>
      </DataTableCell>
      <DataTableCell>
        <span
          className="text-xs truncate block max-w-[200px]"
          style={{ color: 'var(--theme-error-color)' }}
          title={message.failureReason || undefined}
        >
          {message.failureReason || '-'}
        </span>
      </DataTableCell>
    </DataTableRow>
  );
};

/**
 * Campaign Messages Table Component
 * Table showing individual messages with filters and pagination
 */
export const CampaignMessagesTable: React.FC<CampaignMessagesTableProps> = ({
  data,
  isLoading,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onPageChange,
}) => {
  return (
    <Card>
      <CardHeader className="pb-2 py-3 px-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
            Mensagens
          </h3>
          {data && (
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              {(data.total || 0).toLocaleString()} mensagens
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 max-w-xs">
            <Input
              icon={'search'}
              placeholder="Buscar por nome ou telefone"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={onStatusFilterChange}
            />
          </div>
        </div>

        {/* Table */}
        <DataTable>
          <DataTableHeader>
            <DataTableHeadCell>Destinatário</DataTableHeadCell>
            <DataTableHeadCell>Telefone</DataTableHeadCell>
            <DataTableHeadCell>Status</DataTableHeadCell>
            <DataTableHeadCell>Enviada</DataTableHeadCell>
            <DataTableHeadCell>Entregue</DataTableHeadCell>
            <DataTableHeadCell>Lida</DataTableHeadCell>
            <DataTableHeadCell>Erro</DataTableHeadCell>
          </DataTableHeader>
          <DataTableBody>
            {isLoading && (
              <DataTableEmpty colSpan={7} message="Carregando mensagens..." />
            )}
            {!isLoading && (!data || data.items.length === 0) && (
              <DataTableEmpty colSpan={7} message="Nenhuma mensagem encontrada" />
            )}
            {!isLoading && data && data.items.map((message) => (
              <MessageRow key={message.id} message={message} />
            ))}
          </DataTableBody>
        </DataTable>

        {/* Pagination — F50: shared ListPagePagination (numerado, "Mostrando X–Y de N") */}
        {data && (data.total ?? 0) > 0 && (
          <ListPagePagination
            currentPage={data.page}
            totalPages={data.totalPages}
            onPageChange={onPageChange}
            totalItems={data.total}
            pageSize={20}
            itemLabel="mensagens"
          />
        )}
      </CardContent>
    </Card>
  );
};
